import {
  buffers, type EventChannel, eventChannel,
} from 'redux-saga';
import {
  call, delay, put, race, take, takeEvery, takeLatest,
} from 'redux-saga/effects';
import { MAPTIVE_SUPPORT_EMAIL } from '~/_shared/constants/maptive';
import type { GeocodingResponse } from '~/_shared/types/geocoding/geocoding.response';
import type { SpreadsheetDescriptor } from '~/_shared/types/map';
import {
  type GeocodingEventAdditionalFlags,
  type GeocodingEventBroadcastData, type GeocodingStartedEventBroadcastData, registerGeocodingEventBroadcastListener,
} from '~/_shared/utils/api/broadcast.helpers';
import { getEmailDraftHref } from '~/_shared/utils/email/email.helpers';
import { noop } from '~/_shared/utils/function.helpers';
import { select } from '~/_shared/utils/saga/effects';
import { type PickAction } from '~/_shared/utils/types/action.type';
import { MAP_GEOCODING_LIMIT_KEY } from '~/limits/useLimitsChecker';
import {
  getMapData, type MapInfoResponse,
} from '~/map/map.repository';
import { limitNotificationsAddItem } from '~/store/frontendState/limitNotifications/limitNotifications.actionCreators';
import { currentSpreadsheetGeocodingStateSelector } from '~/store/geocoding/geocoding.selectors';
import type {
  GeocodingItem, GeocodingRequest,
} from '~/store/geocoding/geocoding.state';
import type { MapInfoAction } from '~/store/mapInfo/mapInfo.action';
import { MAP_INFO_FETCH_DATA_SUCCESS } from '~/store/mapInfo/mapInfo.actionTypes';
import { createMapInfoFromMapInfoResponse } from '~/store/mapInfo/mapInfo.factory';
import { clientIdSelector } from '~/store/selectors/useClientIdSelector';
import { userDataEmailSelector } from '~/store/userData/userData.selectors';
import { translate } from '~/translations/Trans';
import { mapInfoUpdateBadDataCount } from '../mapInfo/mapInfo.actionCreators';
import { USER_WEB_SOCKET_BROADCAST_INITIALIZED } from '../userData/userData.actionTypes';
import { type GeocodingAction } from './geocoding.action';
import {
  geocodingPauseError,
  geocodingPauseSuccess,
  geocodingProcessingSuccess,
  geocodingResumeError,
  geocodingResumeSuccess,
  geocodingSetIsPauseResumeLoading,
  geocodingSetProgress,
  geocodingStartTracking,
  geocodingWSEventReceived,
} from './geocoding.actionCreators';
import {
  GEOCODING_PAUSE_REQUEST, GEOCODING_RESUME_REQUEST, GEOCODING_START_TRACKING,
} from './geocoding.actionTypes';
import {
  pauseGeocoding, startGeocoding,
} from './geocoding.repository';

const POLLING_DELAY = 60_000;

export function* geocodingSagas() {
  yield takeLatest(USER_WEB_SOCKET_BROADCAST_INITIALIZED, initializeWebSocketChannel);
  yield takeLatest(MAP_INFO_FETCH_DATA_SUCCESS, onMapInfoFetchSuccess);
  yield takeEvery(GEOCODING_START_TRACKING, onGeocodingItemAdded);
  yield takeEvery(GEOCODING_PAUSE_REQUEST, onGeocodingPauseRequest);
  yield takeEvery(GEOCODING_RESUME_REQUEST, onGeocodingResumeRequest);
}

function* initializeWebSocketChannel() {
  // Create the main websocket channel that receives raw messages
  const webSocketsChannel: EventChannel<GeocodingListenerMessage> = yield call(eventChannel<GeocodingListenerMessage>, (emitter) => {
    registerGeocodingEventBroadcastListener({
      onEvent: (data) => emitter({ type: 'change', realSpreadsheetId: data.real_spreadsheet_id, data }),
      onStarted: (data) => emitter({ type: 'started', realSpreadsheetId: data.realSpreadsheetId, data }),
      onError: noop,
    });

    return noop;
  }, buffers.expanding());

  while (true) {
    const message: GeocodingListenerMessage | undefined = yield take(webSocketsChannel);

    if (message) {
      switch (message.type) {
        case 'started': {
          const virtualSpreadsheetId = message.data.virtualSpreadsheets[0];
          if (!virtualSpreadsheetId) {
            break;
          }

          yield call(
            updateProgress,
            {
              progress: message.data.percentage_progress,
              realSpreadsheetId: message.data.realSpreadsheetId,
              triggeredByUserId: message.data.userId,
            },
            virtualSpreadsheetId,
            0,
          );
          break;
        }
        case 'change': {
          yield call(
            updateProgress,
            {
              progress: message.data.percentage,
              realSpreadsheetId: message.data.real_spreadsheet_id,
              triggeredByUserId: message.data.user_id,
            },
            message.data.virtual_id,
            message.data.counters.processed.failed,
            message.data.additional,
          );
          break;
        }
        default:
      }

      yield put(geocodingWSEventReceived(message));
    }
  }
}

function* onMapInfoFetchSuccess(action: PickAction<MapInfoAction, typeof MAP_INFO_FETCH_DATA_SUCCESS>) {
  const spreadsheets: ReadonlyArray<SpreadsheetDescriptor> | undefined = action.payload.data.spreadsheets;
  const spreadsheetData: Maybe<SpreadsheetDescriptor> = spreadsheets?.find(spreadsheet => spreadsheet.isPrimary);

  const geocoding = spreadsheetData?.geocoding;

  if (!spreadsheetData || !geocoding || !geocoding.in_progress) {
    return;
  }

  for (const spreadsheet of spreadsheetData.realSpreadsheets) {
    const realSpreadsheetId = spreadsheet.realSpreadSheetId;
    const { percentage, triggeredByUserId } = getProgress(realSpreadsheetId, geocoding);

    yield put(geocodingStartTracking({
      realSpreadsheetId,
      triggeredByUserId: triggeredByUserId ?? null,
      progress: percentage ?? 0,
      isPaused: geocoding.is_paused,
    }));
  }
}

function* onGeocodingItemAdded(action: PickAction<GeocodingAction, typeof GEOCODING_START_TRACKING>) {
  const realSpreadsheetId = action.payload.item.realSpreadsheetId;
  while (true) {

    const { messageAction }: { messageAction: ReturnType<typeof geocodingWSEventReceived> | undefined } = yield race({
      messageAction: take(
        (reduxAction: Action) => (
          geocodingWSEventReceived.match(reduxAction) &&
          reduxAction.payload.realSpreadsheetId === realSpreadsheetId
        ),
      ),
      timeout: delay(POLLING_DELAY),
    });
    const message: GeocodingListenerMessage | undefined = messageAction?.payload;

    if (message) {
      switch (message.type) {
        case 'change': {
          if (message.data.percentage === 100) {
            return;
          }
          break;
        }
        default:
      }
    }
    else {
      const clientId: number | null = yield select<number | null>(clientIdSelector);
      const mapId: number | null = yield select(state => state.map.mapInfo.data?.id ?? null);

      if (mapId === null || clientId === null) {
        break;
      }

      const geocodingState: GeocodingRequest | null | undefined = yield select(currentSpreadsheetGeocodingStateSelector);
      if (geocodingState?.paused.isPaused) {
        continue;
      }

      const response: { data: MapInfoResponse } = yield call(getMapData, clientId, mapId);
      const data = createMapInfoFromMapInfoResponse(response.data);
      const spreadsheetData: Maybe<SpreadsheetDescriptor> = data.spreadsheets?.find(spreadsheet =>
        spreadsheet.realSpreadsheets.some(spreadSheetId => spreadSheetId.realSpreadSheetId === action.payload.item.realSpreadsheetId)
      );

      const geocoding = spreadsheetData?.geocoding;
      const geocodingDetails = geocoding?.geocodings?.[action.payload.item.realSpreadsheetId];

      if (!spreadsheetData || !geocoding || !geocodingDetails) {
        break;
      }

      yield updateProgress(
        {
          progress: geocodingDetails.percentage,
          realSpreadsheetId: action.payload.item.realSpreadsheetId,
          triggeredByUserId: action.payload.item.triggeredByUserId,
          isPaused: geocoding.is_paused,
        },
        spreadsheetData.spreadSheetId,
        geocoding.bad_data,
      );

      if (geocodingDetails.percentage === 100) {
        break;
      }
    }
  }
}

function* geocodingChangeConcurrencyPreventionWrapper(realSpreadsheetId: number, change: Generator) {
  const isChangeInProgress: boolean | undefined = yield select(
    state => state.spreadsheet.geocoding.perSpreadsheetGeocodingRequest[realSpreadsheetId]?.paused.changeRequestSent
  );

  if (isChangeInProgress) {
    return;
  }

  yield put(geocodingSetIsPauseResumeLoading(realSpreadsheetId));
  yield* change;

  return;
}

function* pause(action: PickAction<GeocodingAction, typeof GEOCODING_PAUSE_REQUEST>) {
  const realSpreadsheetId = action.payload.realSpreadsheetId;
  try {
    yield call(pauseGeocoding, action.payload.clientId, realSpreadsheetId);
    yield put(geocodingPauseSuccess(realSpreadsheetId));
  }
  catch (e) {
    yield put(geocodingPauseError(realSpreadsheetId));
  }
}

function* resume(action: PickAction<GeocodingAction, typeof GEOCODING_RESUME_REQUEST>) {
  const realSpreadsheetId = action.payload.realSpreadsheetId;
  try {
    yield call(startGeocoding, action.payload.clientId, realSpreadsheetId);
    yield put(geocodingResumeSuccess(realSpreadsheetId));
  }
  catch (e) {
    yield put(geocodingResumeError(realSpreadsheetId));
  }
}

function* onGeocodingPauseRequest(action: PickAction<GeocodingAction, typeof GEOCODING_PAUSE_REQUEST>) {
  yield* geocodingChangeConcurrencyPreventionWrapper(action.payload.realSpreadsheetId, pause(action));
}

function* onGeocodingResumeRequest(action: PickAction<GeocodingAction, typeof GEOCODING_RESUME_REQUEST>) {
  yield* geocodingChangeConcurrencyPreventionWrapper(action.payload.realSpreadsheetId, resume(action));
}

function* updateProgress(item: GeocodingItem, virtualSpreadsheetId: number, failedCount: number, additionalFlags: GeocodingEventAdditionalFlags | null = null) {
  yield put(geocodingSetProgress({
    [item.realSpreadsheetId]: item,
  }));

  if (item.progress === 100) {
    yield put(geocodingProcessingSuccess(new Set([item.realSpreadsheetId])));
  }

  yield put(mapInfoUpdateBadDataCount(virtualSpreadsheetId, failedCount));

  if (failedCount > 0 && additionalFlags?.geocoding_limit_exceeded) {
    const clientEmail: string = yield select<string>(userDataEmailSelector);

    const contactSupportEmailLink = getEmailDraftHref(
      MAPTIVE_SUPPORT_EMAIL,
      translate('Account Limit Increase'),
      translate('I would like to increase limits for my account {{clientEmail}}', { clientEmail })
    );

    yield put(limitNotificationsAddItem({
      type: 'info',
      key: MAP_GEOCODING_LIMIT_KEY,
      heading: translate('Map Processing Paused'),
      text: translate('limits.locationsLimitExceededIncreaseLimit', { count: failedCount }),
      button: {
        text: translate('Contact Support'),
        href: contactSupportEmailLink,
      },
    }));
  }
}

function getProgress(realSpreadsheetId: number, geocoding: GeocodingResponse) {
  const geocodingDataForSpreadsheet = geocoding.geocodings[realSpreadsheetId];

  return { percentage: geocodingDataForSpreadsheet?.percentage, triggeredByUserId: geocodingDataForSpreadsheet?.caller?.id };
}

export type GeocodingListenerMessage = {
  type: 'change';
  realSpreadsheetId: number;
  data: GeocodingEventBroadcastData;
} | {
  type: 'started';
  realSpreadsheetId: number;
  data: GeocodingStartedEventBroadcastData;
};
